import {
  dedupePanelChanges,
  formatChangeValue,
  formatToggleLabel,
  getPanelChangeActorKey,
  getToggleTransitionLabels,
  isEnabledToggleChange,
  readToggleState,
  collectPanelChangeActors,
} from './panelChangeLog';
import {buildSettingKey} from './panelChangeSettingKey';

const normalizeEntity = entity => String(entity || '').toLowerCase();

const isTagLevelChange = change => normalizeEntity(change.entity) === 'tag_attribute';

const isAdUnitEntityChange = change => normalizeEntity(change.entity) === 'adunit';

const isEnabledToggle = isEnabledToggleChange;

/** One summary card per attribute (e.g. Price adjustments · Adagio). */
const buildAttributeKey = change => buildSettingKey(change);

const buildTransitionKey = (oldValue, newValue) => `${oldValue}|${newValue}`;

const formatScopeLabel = (adUnitCount, isTagLevel) => {
  if (isTagLevel) return 'Tag · all linked ad units';
  if (adUnitCount <= 1) return '1 ad unit';
  return `${adUnitCount} ad units`;
};

const collectAdUnit = (map, change) => {
  if (change?.adunitId == null) return;
  if (!map.has(change.adunitId)) {
    map.set(change.adunitId, {
      adunitId: change.adunitId,
      adName: change.adName || null,
      domainId: change.domainId ?? null,
      publisherId: change.publisherId ?? null,
    });
  }
};

const toAdUnitsList = map =>
  Array.from(map.values()).sort((a, b) =>
    String(a.adName || a.adunitId).localeCompare(String(b.adName || b.adunitId))
  );

const formatAdUnitEntityTitle = change => {
  switch (change.entityColumn) {
    case 'name':
      return 'Ad unit renamed';
    case 'weight':
      return 'Ad unit weight updated';
    case 'as_tag_id':
      return 'Ad unit tag reassigned';
    default:
      return `Ad unit · ${change.entityColumn || 'updated'}`;
  }
};

const getBulletAttName = change => {
  if (isAdUnitEntityChange(change)) return formatAdUnitEntityTitle(change);
  if (isEnabledToggle(change)) {
    return formatToggleLabel(readToggleState(change.entityValue) || 'enabled') || 'Enabled';
  }
  return change.attName || change.entityColumn || 'Attribute';
};

const shouldShowTransitionValues = (oldValue, newValue, isToggle) => {
  if (isToggle) return true;
  if (oldValue === newValue) return false;
  return !(oldValue === '—' && newValue === '—');
};

const compareAttributeGroups = (a, b) => {
  if (a.isTagLevel !== b.isTagLevel) return a.isTagLevel ? -1 : 1;
  if (b.adUnitCount !== a.adUnitCount) return b.adUnitCount - a.adUnitCount;
  const aTitle = [a.attGroupName, a.attName].filter(Boolean).join(' · ');
  const bTitle = [b.attGroupName, b.attName].filter(Boolean).join(' · ');
  return aTitle.localeCompare(bTitle);
};

const compareTransitions = (a, b) => {
  if (b.adUnitCount !== a.adUnitCount) return b.adUnitCount - a.adUnitCount;
  return buildTransitionKey(a.oldValue, a.newValue).localeCompare(
    buildTransitionKey(b.oldValue, b.newValue)
  );
};

/**
 * Build an overview for one chart bucket (day / hour / 15s window).
 * Groups by attribute, with per old→new transition counts underneath.
 */
export const buildPanelChangeSummary = changes => {
  const uniqueChanges = dedupePanelChanges(changes);
  if (uniqueChanges.length === 0) {
    return {bullets: []};
  }

  const attributeGroups = new Map();

  for (const change of uniqueChanges) {
    const attributeKey = buildAttributeKey(change);
    const isToggle = isEnabledToggle(change);
    const toggleLabels = isToggle ? getToggleTransitionLabels(change) : null;
    const oldValue = toggleLabels?.oldValue ?? formatChangeValue(change.oldValue);
    const newValue = toggleLabels?.newValue ?? formatChangeValue(change.entityValue);
    const transitionKey = buildTransitionKey(oldValue, newValue);

    let attributeGroup = attributeGroups.get(attributeKey);
    if (!attributeGroup) {
      attributeGroup = {
        id: attributeKey,
        attGroupName: change.attGroupName || null,
        attGroupCode: change.attGroupCode || null,
        attGroupIsSsp: change.attGroupIsSsp ?? null,
        attName: getBulletAttName(change),
        isEnabledToggle: isEnabledToggle(change),
        isTagLevel: isTagLevelChange(change),
        adUnits: new Map(),
        transitions: new Map(),
        actorSamples: new Map(),
        sampleChange: null,
      };
      attributeGroups.set(attributeKey, attributeGroup);
    }

    collectAdUnit(attributeGroup.adUnits, change);
    attributeGroup.actorSamples.set(getPanelChangeActorKey(change), change);
    if (!attributeGroup.sampleChange) attributeGroup.sampleChange = change;

    let transition = attributeGroup.transitions.get(transitionKey);
    if (!transition) {
      transition = {
        id: transitionKey,
        oldValue,
        newValue,
        showValues: shouldShowTransitionValues(oldValue, newValue, isToggle),
        isToggle,
        valueTone: toggleLabels?.valueTone ?? 'default',
        adUnits: new Map(),
        actorSamples: new Map(),
        sampleChange: null,
      };
      attributeGroup.transitions.set(transitionKey, transition);
    }
    collectAdUnit(transition.adUnits, change);
    transition.actorSamples.set(getPanelChangeActorKey(change), change);
    if (!transition.sampleChange) transition.sampleChange = change;
  }

  const bullets = Array.from(attributeGroups.values())
    .map(group => {
      const groupAdUnits = toAdUnitsList(group.adUnits);
      const rawTransitions = Array.from(group.transitions.values());

      const transitions = group.isEnabledToggle
        ? [
            {
              id: `${group.id}|toggle`,
              oldValue: '',
              newValue: group.attName,
              showValues: true,
              isToggle: true,
              valueTone:
                group.attName === 'Disabled'
                  ? 'disabled'
                  : group.attName === 'Enabled'
                    ? 'enabled'
                    : 'default',
              adUnits: groupAdUnits,
              adUnitCount: groupAdUnits.length,
              scope: formatScopeLabel(groupAdUnits.length, group.isTagLevel),
              actors: collectPanelChangeActors([...group.actorSamples.values()]),
            },
          ]
        : rawTransitions
            .map(transition => {
              const adUnits = toAdUnitsList(transition.adUnits);
              return {
                id: transition.id,
                oldValue: transition.oldValue,
                newValue: transition.newValue,
                showValues: transition.showValues,
                isToggle: transition.isToggle,
                valueTone: transition.valueTone,
                adUnits,
                adUnitCount: adUnits.length,
                scope: formatScopeLabel(adUnits.length, group.isTagLevel),
                actors: collectPanelChangeActors([...transition.actorSamples.values()]),
              };
            })
            .filter(transition => transition.isToggle || transition.showValues)
            .sort(compareTransitions);

      return {
        id: group.id,
        attGroupName: group.attGroupName,
        attGroupCode: group.attGroupCode,
        attGroupIsSsp: group.attGroupIsSsp,
        attName: group.attName,
        isEnabledToggle: group.isEnabledToggle,
        isTagLevel: group.isTagLevel,
        adUnits: groupAdUnits,
        adUnitCount: groupAdUnits.length,
        scope: formatScopeLabel(groupAdUnits.length, group.isTagLevel),
        transitions,
      };
    })
    .sort(compareAttributeGroups);

  return {bullets};
};
